//    Copyright 2017 Google
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.

/// <reference path="../../../node_modules/@types/applepayjs/index.d.ts" />

export class ApplePaymentRequest {
  private APPLE_PAY_JS_IDENTIFIER = 'https://apple.com/apple-pay';
  private paymentRequest: ApplePayJS.ApplePayPaymentRequest;
  public paymentRequestID: string = '';
  public shippingAddress: PaymentAddress | null = null;
  public shippingOption: string = '';
  public shippingType: string = 'shipping';
  private session: ApplePaySession;
  private paymentResolver: any;
  private paymentRejector: any;
  public onshippingaddresschange: any;
  public onshippingoptionchange: any;
  public onpaymentmethodselected: any;
  public onvalidatemerchant: any;

  private validationEndpoint: string = '';
  private merchantIdentifier: string = '';
  constructor(
    methodData: PaymentMethodData[],
    details: PaymentDetailsInit,
    options: PaymentOptions
  ) {
    let methodSpecified = false;
    this.paymentRequest = {
      countryCode: '',
      currencyCode: '',
      lineItems: [],
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: [],
      total: {
        type: 'final',
        label: '',
        amount: '0'
      },
      billingContact: undefined,
      requiredBillingContactFields: [],
      requiredShippingContactFields: [],
      shippingContact: undefined,
      shippingMethods: <ApplePayJS.ApplePayShippingMethod[]>[],
      shippingType: 'shipping',
    };

    // methodData
    for (let method of methodData) {
      // If `supportedMethods` includes `https://apple.com/apple-pay`...
      if (method.supportedMethods.indexOf(this.APPLE_PAY_JS_IDENTIFIER) > -1) {
        console.info('method', method);
        this.paymentRequest.supportedNetworks = method.data.supportedNetworks;
        this.paymentRequest.countryCode = method.data.countryCode;
        console.info('paymentRequest', this.paymentRequest);
        if (method.data.billingContact) {
          this.paymentRequest.billingContact = method.data.billingContact;
        } else {
          delete this.paymentRequest.billingContact;
        }
        if (method.data.shippingContact) {
          this.paymentRequest.shippingContact = method.data.shippingContact;
        } else {
          delete this.paymentRequest.shippingContact;
        }
        if (method.data.merchantCapabilities) {
          this.paymentRequest.merchantCapabilities = method.data.merchantCapabilities;
        }

        this.validationEndpoint = method.data.validationEndpoint;
        this.merchantIdentifier = method.data.merchantIdentifier;
        methodSpecified = true;
        break;
      }
    }

    if (!methodSpecified) {
      throw 'Payment method not specified for Apple Pay.';
    }

    // details
    if (details) {
      this.updatePaymentDetails(details);
    }

    // options
    if (options) {
      if (options.requestShipping && this.paymentRequest.requiredBillingContactFields && this.paymentRequest.requiredShippingContactFields) {
        this.paymentRequest.requiredBillingContactFields.push('postalAddress');
        this.paymentRequest.requiredShippingContactFields.push('postalAddress');
      }
      if (options.requestPayerName && this.paymentRequest.requiredShippingContactFields) {
        this.paymentRequest.requiredShippingContactFields.push('name');
      }
      if (options.requestPayerEmail && this.paymentRequest.requiredShippingContactFields) {
        this.paymentRequest.requiredShippingContactFields.push('email');
      }
      if (options.requestPayerPhone && this.paymentRequest.requiredShippingContactFields) {
        this.paymentRequest.requiredShippingContactFields.push('phone');
      }
      if (options.shippingType === 'pickup') {
        this.paymentRequest.shippingType = 'servicePickup';
      } else {
        this.paymentRequest.shippingType = <ApplePayJS.ApplePayShippingType>options.shippingType || 'shipping';
      }
    }

    this.session = new ApplePaySession(3, this.paymentRequest);

    this.session.addEventListener('validatemerchant', (e: any) =>
      this.onValidateMerchant(e)
    );

    this.session.addEventListener('paymentauthorized', (e: any) =>
      this.onPaymentAuthorized(e)
    );

    this.session.addEventListener('paymentmethodselected', (e: any) =>
      this.onPaymentMethodSelected(e)
    );

    this.session.addEventListener('shippingcontactselected', (e: any) =>
      this.onShippingAddressChange(e)
    );

    this.session.addEventListener('shippingmethodselected', (e: any) =>
      this.onShippingOptionChange(e)
    );

    this.session.addEventListener('cancel', (_e: Event) =>
      this.onPaymentCanceled()
    );
    /*
    this.session.addEventListener('validatemerchant',
      this.onValidateMerchant.bind(this));
    this.session.addEventListener('paymentauthorized',
      this.onPaymentAuthorized.bind(this));
    this.session.addEventListener('paymentmethodselected',
      this.onPaymentMethodSelected.bind(this));
    this.session.addEventListener('shippingcontactselected',
      this.onShippingAddressChange.bind(this));
    this.session.addEventListener('shippingmethodselected',
      this.onShippingOptionChange.bind(this));
    this.session.addEventListener('cancel',
      this.onPaymentCanceled.bind(this));
      */
  }


  private updatePaymentDetails(details: PaymentDetailsUpdate) {
    if (details.displayItems) {
      this.paymentRequest.lineItems = <ApplePayJS.ApplePayLineItem[]>[];
      for (let item of details.displayItems) {
        let lineItem: ApplePayJS.ApplePayLineItem = {
          type: item.pending === true ? 'pending' : 'final',
          label: item.label,
          amount: item.amount.value
        }
        this.paymentRequest.lineItems.push(lineItem);
      }
    }

    if (details.shippingOptions) {
      this.paymentRequest.shippingMethods = <ApplePayJS.ApplePayShippingMethod[]>[];
      for (let option of details.shippingOptions) {
        let shippingMethod: ApplePayJS.ApplePayShippingMethod = {
          label: option.label,
          detail: option.label,
          amount: option.amount.value,
          identifier: option.id
        };
        this.paymentRequest.shippingMethods.push(shippingMethod);
      }
    }

    if (details.total) {
      this.paymentRequest.currencyCode = details.total.amount.currency;
      this.paymentRequest.total = {
        type: details.total.pending === true ? 'pending' : 'final',
        label: details.total.label,
        amount: details.total.amount.value
      };
    } else {
      throw '`total` is required parameter for `PaymentDetailsUpdate`.';
    }
  }

  /**
   * @param  {ApplePayJS.ApplePayPaymentMethod} paymentMethod
   */
  private updatePaymentMethod(paymentMethod: ApplePayJS.ApplePayPaymentMethod) {

  }

  /**
   * @param  {ApplePayJS.ApplePayPaymentContact} shippingContact
   */
  /*
    private convertPaymentAddress(contact: ApplePayJS.ApplePayPaymentContact): any {
      let address = {
        country: '',
        addressLine: [],
        region: '',
        city: '',
        dependentLocality: '',
        postalCode: '',
        sortingCode: '',
        languageCode: '',
        organization: '',
        recipient: '',
        phone: ''
      };
      if (contact !== undefined) {
        let address = {
          country: contact.countryCode || '',
          addressLine: contact.addressLines || [],
          region: contact.administrativeArea || '',
          city: contact.locality || '',
          dependentLocality: '',
          postalCode: contact.postalCode || '',
          sortingCode: contact.country || '',
          languageCode: '',
          organization: '',
          recipient: `${contact.givenName} ${contact.familyName}`,
          phone: contact.phoneNumber || ''
        }
      }
      return address;
    }
    */
  private convertPaymentAddress(contact: ApplePayJS.ApplePayPaymentContact | undefined | null): any {
    if (!contact) return {
      country: '',
      addressLine: [],
      region: '',
      city: '',
      dependentLocality: '',
      postalCode: '',
      sortingCode: '',
      languageCode: '',
      organization: '',
      recipient: '',
      phone: ''
    };

    return {
      country: contact.countryCode || '',
      addressLine: contact.addressLines || [],
      region: contact.administrativeArea || '',
      city: contact.locality || '',
      dependentLocality: '',
      postalCode: contact.postalCode || '',
      sortingCode: contact.country || '',
      languageCode: '',
      organization: '',
      recipient: `${contact.givenName} ${contact.familyName}`,
      phone: contact.phoneNumber || ''
    };
  }

  /**
   * @param  {ApplePayJS.ApplePayShippingMethod} shippingMethod
   */
  private convertShippingMethod(shippingMethod: ApplePayJS.ApplePayShippingMethod | undefined): string {
    if (this.paymentRequest.shippingMethods && shippingMethod) {
      for (let method of this.paymentRequest.shippingMethods) {
        if (shippingMethod.identifier === method.identifier) {
          return method.identifier;
        }
      }
    }
    return '';
  }

  /**
   * @param {ApplePayJS.ApplePayPayment} payment
   * @returns {any} response
   */
  private convertPaymentResponse(payment: ApplePayJS.ApplePayPayment): any {
    console.log('convertPaymentResponse',payment);
    let shippingAddress = this.convertPaymentAddress(payment.shippingContact);
    let billingAddress = this.convertPaymentAddress(payment.billingContact);
    let response = {
      details: {
        billingAddress: billingAddress
      },
      methodName: this.APPLE_PAY_JS_IDENTIFIER,
      payerEmail: payment.shippingContact ? payment.shippingContact.emailAddress : '',
      payerName: payment.billingContact ? `${payment.billingContact.givenName} ${payment.billingContact.familyName}` : '',
      payerPhone: payment.shippingContact ? payment.shippingContact.phoneNumber : '',
      shippingAddress: shippingAddress,
      shippingOption: '',
      applePayRaw: payment,
      complete: this.onPaymentComplete.bind(this)
    };
    return response;
  }

  /**
   * @returns Promise
   */
  public show(): Promise<PaymentResponse> {
    this.session.begin();
    return new Promise((resolve, reject) => {
      //this.paymentResolver = (response: ApplePayJS.ApplePayPayment) => {
      //  // response.complete = this.onPaymentComplete;
      //  resolve(<any>response);
      //};
      this.paymentResolver = (response: ApplePayJS.ApplePayPayment) => {
        console.log('paymentResolver', response);
        const converted = this.convertPaymentResponse(response);
        resolve(converted as PaymentResponse);
      };
      this.paymentRejector = (error: Error) => {
        reject(error);
      };
    });
  }

  /**
   * @returns Promise
   */
  public abort(): void {
    // TODO: Should this return a promise?
    // TODO: Does `cancel` event fire by itself?
    this.session.abort();
  }

  /**
   * @returns Promise
   */
  /*
  public canMakePayment(): Promise<boolean> {
    if (this.merchantIdentifier) {
      return ApplePaySession.canMakePaymentsWithActiveCard(this.merchantIdentifier);
    } else {
      throw '`merchantIdentifier` is not specified.';
    }
  }
  */
  public canMakePayment(): Promise<boolean> {
    if ((window as any).ApplePaySession) {
      return Promise.resolve(ApplePaySession.canMakePayments());
    }
    return Promise.resolve(false);
  }

  public completeMerchantValidation(merchantSession: any): void {
    // https://developer.apple.com/reference/applepayjs/applepaysession/1778015-completemerchantvalidation
    this.session.completeMerchantValidation(merchantSession);
  }

  public completePaymentMethodSelection(newTotal: ApplePayJS.ApplePayLineItem, newLineItems: ApplePayJS.ApplePayLineItem[]): void {
    // https://developer.apple.com/reference/applepayjs/applepaysession/1777995-completepaymentmethodselection
    this.session.completePaymentMethodSelection(newTotal, newLineItems);
  }

  /**
   * @param  {string} type
   * @param  {(e:Event)=>any} callback
   * @returns void
   */
  public addEventListener(type: string, callback: (e: Event) => any): void {
    if (type === 'shippingaddresschange') {
      this.onshippingaddresschange = callback;
    }
    else if (type === 'shippingoptionchange') {
      this.onshippingoptionchange = callback;
    }
    else if (type === 'paymentmethodselected') {
      this.onpaymentmethodselected = callback;
    }
    else if (type === 'validatemerchant') {
      this.onvalidatemerchant = callback;
    }
    else {
      throw `Unknown event type "${type}" for \`addEventListener\`.`;
    }

    /*if (type === 'shippingaddresschange' ||
      type === 'shippingoptionchange' ||
      type === 'paymentmethodselected' ||
      type === 'validatemerchant') {
      this[`on${type}`] = callback;
    } else {
      throw `Unknown event type "${type}" for \`addEventListener\`.`;
    }*/
  }
  /*
type ApplePayEventCallback =
  | ((e: ApplePayJS.ApplePayShippingContactSelectedEvent | ApplePayJS.ApplePayShippingMethodSelectedEvent | ApplePayJS.ApplePayPaymentMethodSelectedEvent | ApplePayJS.ApplePayValidateMerchantEvent) => void);

public addEventListener(type: 'shippingaddresschange' | 'shippingoptionchange' | 'paymentmethodselected' | 'validatemerchant', callback: ApplePayEventCallback): void {
  switch(type) {
    case 'shippingaddresschange':
  this.onshippingaddresschange = callback as (e: ApplePayJS.ApplePayShippingContactSelectedEvent) => void;
  break;
  case 'shippingoptionchange':
  this.onshippingoptionchange = callback as (e: ApplePayJS.ApplePayShippingMethodSelectedEvent) => void;
  break;
  case 'paymentmethodselected':
  this.onpaymentmethodselected = callback as (e: ApplePayJS.ApplePayPaymentMethodSelectedEvent) => void;
  break;
  case 'validatemerchant':
  this.onvalidatemerchant = callback as (e: ApplePayJS.ApplePayValidateMerchantEvent) => void;
  break;
  default:
      throw new Error(`Unknown event type "${type}" for addEventListener.`);
}
}
  */

  /**
   * @param  {ApplePayJS.ApplePayValidMerchantEvent} e
   * @returns void
   */
  private onValidateMerchant(
    e: ApplePayJS.ApplePayValidateMerchantEvent
  ): void {
    e.stopPropagation();

    console.info('Validating merchant...', e.validationURL);
    // https://developer.apple.com/reference/applepayjs/applepaysession/1778021-onvalidatemerchant
    if (this['onvalidatemerchant']) {
      this['onvalidatemerchant'](e);
    } else {
      let headers = new Headers({
        'Content-Type': 'application/json'
      });
      fetch(this.validationEndpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ validationURL: e.validationURL })
      })
        .then(res => {
          if (res.status !== 200) {
            throw 'Merchant validation error.';
          }
          return res.json();
        })
        .then((merchantSession: any) => {
          // 🔥 KEY FIX: unwrap if backend wraps response
          const session = merchantSession.data || merchantSession;

          console.info('Merchant validation success', session);

          this.completeMerchantValidation(session);
        })
        .catch(error => {
          console.error('Merchant validation failed', error);
          this.session.abort();
        });
      /*fetch(this.validationEndpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ validationURL: e.validationURL })
      }).then(res => {
        if (res.status === 200) {
          return res.json();
        } else {
          throw 'Merchant validation error.';
        }
      }).then((merchantSession: any) => {
        this.completeMerchantValidation(merchantSession);
      }).catch(error => {
        throw error;
      });*/
    }
  }

  /**
   * @param  {ApplePayJS.ApplePayPaymentMethodSelectedEvent} e
   * @returns void
   */
  private onPaymentMethodSelected(
    e: ApplePayJS.ApplePayPaymentMethodSelectedEvent
  ): void {
    e.stopPropagation();
    // https://developer.apple.com/reference/applepayjs/applepaysession/1778013-onpaymentmethodselected
    if (this['onpaymentmethodselected']) {
      this['onpaymentmethodselected'](e);
    } else {
      let newTotal = this.paymentRequest.total;
      let newLineItems = this.paymentRequest.lineItems ? this.paymentRequest.lineItems : [];
      this.session.completePaymentMethodSelection(newTotal, newLineItems);
    }
  }

  /**
   * @param  {ApplePayJS.ApplePayShippingContactSelectedEvent} e
   * @returns void
   */
  private onShippingAddressChange(
    e: ApplePayJS.ApplePayShippingContactSelectedEvent
  ): void {
    if (!this['onshippingaddresschange']) return;
    e.stopPropagation();

    // Convert ApplePay ShippingContact into PaymentRequest PaymentAddress
    // https://developer.apple.com/reference/applepayjs/applepaysession/1778009-onshippingcontactselected
    let shippingContact = e.shippingContact;
    this.shippingAddress = this.convertPaymentAddress(shippingContact);

    this['onshippingaddresschange']({
      updateWith: (p: any) => {
        p.then((details: PaymentDetailsUpdate) => {
          // https://developer.apple.com/reference/applepayjs/applepaysession/1778008-completeshippingcontactselection
          this.updatePaymentDetails(details);
          this.session.completeShippingContactSelection(
            ApplePaySession.STATUS_SUCCESS,
            this.paymentRequest.shippingMethods ? this.paymentRequest.shippingMethods : [],
            this.paymentRequest.total,
            this.paymentRequest.lineItems ? this.paymentRequest.lineItems : []);
        }, (details: PaymentDetailsUpdate) => {
          // TODO: In which case does this happen?
          this.updatePaymentDetails(details);
          this.session.completeShippingContactSelection(
            ApplePaySession.STATUS_FAILURE,
            this.paymentRequest.shippingMethods ? this.paymentRequest.shippingMethods : [],
            this.paymentRequest.total,
            this.paymentRequest.lineItems ? this.paymentRequest.lineItems : []);
        });
      }
    });
  }

  /**
   * @param  {ApplePayJS.ApplePayShippingMethodSelectedEvent} e
   * @returns void
   */
  private onShippingOptionChange(
    e: ApplePayJS.ApplePayShippingMethodSelectedEvent
  ): void {
    if (!this.onshippingoptionchange) return;
    e.stopPropagation();

    // Convert ApplePay ShippingMethod into PaymentRequest PaymentShippingOption id
    const shippingMethod = e.shippingMethod;
    this.shippingOption = this.convertShippingMethod(shippingMethod);

    // Call the user-defined callback
    this.onshippingoptionchange({
      updateWith: (detailsPromise: Promise<PaymentDetailsUpdate>) => {
        detailsPromise
          .then((details: PaymentDetailsUpdate) => {
            // Update payment details
            this.updatePaymentDetails(details);

            // Complete shipping method selection successfully
            this.session.completeShippingMethodSelection(
              ApplePaySession.STATUS_SUCCESS,
              this.paymentRequest.total!,
              this.paymentRequest.lineItems ?? []
            );
          })
          .catch((details: PaymentDetailsUpdate) => {
            // In case of failure
            this.updatePaymentDetails(details);
            this.session.completeShippingMethodSelection(
              ApplePaySession.STATUS_FAILURE,
              {
                amount: '0.00',
                label: this.paymentRequest.total!.label,
                type: 'final'
              },
              []
            );
          });
      },
      //status: number, newTotal: ApplePayJS.ApplePayLineItem, newLineItems: ApplePayJS.ApplePayLineItem[]
    });
  }
  /*
  private onShippingOptionChange(
    e: ApplePayJS.ApplePayShippingMethodSelectedEvent
  ): void {
    if (!this['onshippingoptionchange']) return;
    e.stopPropagation();

    // Convert ApplePay ShippingMethod into PaymentRequest PaymentShippingOption id
    // https://developer.apple.com/reference/applepayjs/applepaysession/1778028-onshippingmethodselected
    let shippingMethod = e.shippingMethod;
    this.shippingOption = this.convertShippingMethod(shippingMethod);

    this['onshippingoptionchange']({
      updateWith: p => {
        p.then((details: PaymentDetailsUpdate) => {
          // https://developer.apple.com/reference/applepayjs/applepaysession/1778024-completeshippingmethodselection
          this.updatePaymentDetails(details);
          this.session.completeShippingMethodSelection(
            ApplePaySession.STATUS_SUCCESS,
            this.paymentRequest.total,
            this.paymentRequest.lineItems ? this.paymentRequest.lineItems : []);
        }, (details: PaymentDetailsUpdate) => {
          // TODO: In which case does this happen?
          this.updatePaymentDetails(details);
          this.session.completeShippingMethodSelection(
            ApplePaySession.STATUS_FAILURE,
            undefined,
            null
          );
        });
      }
    })
  }
  */
  /**
   * @param  {ApplePayJS.ApplePayPaymentAuthorizedEvent} e
   * @returns void
   */
  private onPaymentAuthorized(
    e: ApplePayJS.ApplePayPaymentAuthorizedEvent
  ): void {
    console.info('Payment authorized event fired', e.payment);

    if (this.paymentResolver) {
      let response = this.convertPaymentResponse(e.payment);

      this.paymentResolver(response);

      this.paymentResolver = null;
      this.paymentRejector = null;
    } else {
      // 🔥 fallback to prevent hanging UI during debugging
      console.warn('No resolver found, auto-completing payment');
      this.session.completePayment(ApplePaySession.STATUS_SUCCESS);
    }
  }
  /*
  private onPaymentAuthorized(
    e: ApplePayJS.ApplePayPaymentAuthorizedEvent
  ): void {
    if (this.paymentResolver) {
      let response = this.convertPaymentResponse(e.payment);
      // https://developer.apple.com/reference/applepayjs/payment
      this.paymentResolver(response);
      this.paymentResolver = null;
      this.paymentRejector = null;
    }
  }
  */
  /**
   * @returns void
   */
  private onPaymentCanceled(): void {
    if (this.paymentRejector) {
      this.paymentRejector();
      this.paymentResolver = null;
      this.paymentRejector = null;
    }
  }

  /**
   * @param  {'success' | 'fail' | 'unknown'} result
   * @returns void
   */
  private onPaymentComplete(result: 'success' | 'fail' | 'unknown'): void {
    if (result === 'success' ||
      result === 'fail' ||
      result === 'unknown' ||
      result === '') {
      let status: number;
      switch (result) {
        case 'success':
          status = ApplePaySession.STATUS_SUCCESS;
          break;
        case 'fail':
          status = ApplePaySession.STATUS_FAILURE;
          break;
        case 'unknown':
          // TODO: Not sure what is the best way to handle this
          // Treat is as success for the time being.
          status = ApplePaySession.STATUS_SUCCESS;
          break;
        default:
          // TODO: Not sure what is the best way to handle this
          // Treat is as success for the time being.
          status = ApplePaySession.STATUS_SUCCESS;
          break;
      }
      // https://developer.apple.com/reference/applepayjs/applepaysession/1778012-completepayment
      this.session.completePayment(status);
    } else {
      throw 'Unknown status code for complete().';
    }
  }
}
